/**
 * useStreamingMarkdown
 *
 * Splits streaming markdown content into a "safe" prefix that can be rendered
 * immediately by ReactMarkdown and a "pending tail" that renders as plain text
 * while the stream is incomplete.
 *
 * Splitting rules (applied in order of precedence):
 * 1. If there's an unclosed ``` fence in the string, split before it
 * 2. If the tail contains an incomplete [...](  link (no closing ) yet), split before [
 * 3. Otherwise split at the last \n within the final 240 characters (soft cap)
 */

const TAIL_CAP = 240;

function findUnclosedFence(content: string): number {
  let pos = 0;
  let fenceCount = 0;
  let lastFenceStart = -1;

  while (pos < content.length) {
    const idx = content.indexOf('```', pos);
    if (idx === -1) break;
    fenceCount += 1;
    if (fenceCount % 2 === 1) {
      // This is an opening fence
      lastFenceStart = idx;
    } else {
      // This is a closing fence
      lastFenceStart = -1;
    }
    pos = idx + 3;
  }

  // If fenceCount is odd, the last opening fence is unclosed
  return lastFenceStart;
}

function findIncompleteLink(tail: string): number {
  // Look for [ that is followed by text but the ] or ( or ) is missing
  let searchPos = 0;
  while (searchPos < tail.length) {
    const bracketOpen = tail.indexOf('[', searchPos);
    if (bracketOpen === -1) break;

    // Find closing ]
    const bracketClose = tail.indexOf(']', bracketOpen);
    if (bracketClose === -1) {
      // No closing bracket — link is incomplete
      return bracketOpen;
    }

    // Check if ( follows ]
    if (tail[bracketClose + 1] !== '(') {
      // Just [text] with no link, not a markdown link in progress
      searchPos = bracketClose + 1;
      // eslint-disable-next-line no-continue
      continue;
    }

    // Find closing )
    const parenClose = tail.indexOf(')', bracketClose + 2);
    if (parenClose === -1) {
      // Link is incomplete — [text](url... with no closing )
      return bracketOpen;
    }

    searchPos = parenClose + 1;
  }

  return -1;
}

export interface StreamingMarkdownResult {
  safeMarkdown: string;
  pendingTail: string;
}

export function useStreamingMarkdown(
  content: string,
  isStreaming: boolean,
): StreamingMarkdownResult {
  if (!isStreaming || !content) {
    return { safeMarkdown: content, pendingTail: '' };
  }

  // Rule 1: unclosed fence — split before the unclosed ```
  const unclosedFencePos = findUnclosedFence(content);
  if (unclosedFencePos !== -1) {
    return {
      safeMarkdown: content.slice(0, unclosedFencePos),
      pendingTail: content.slice(unclosedFencePos),
    };
  }

  // For rules 2 and 3, only look at the tail (last TAIL_CAP chars)
  const tailStart = Math.max(0, content.length - TAIL_CAP);
  const tail = content.slice(tailStart);

  // Rule 2: incomplete markdown link in the tail
  const incompleteLinkPos = findIncompleteLink(tail);
  if (incompleteLinkPos !== -1) {
    const splitAt = tailStart + incompleteLinkPos;
    return {
      safeMarkdown: content.slice(0, splitAt),
      pendingTail: content.slice(splitAt),
    };
  }

  // Rule 3: split at the last \n in the tail (soft cap)
  const lastNewline = tail.lastIndexOf('\n');
  if (lastNewline !== -1) {
    const splitAt = tailStart + lastNewline + 1; // keep the newline in safe portion
    return {
      safeMarkdown: content.slice(0, splitAt),
      pendingTail: content.slice(splitAt),
    };
  }

  // No split point found — the whole tail is pending
  return {
    safeMarkdown: content.slice(0, tailStart),
    pendingTail: tail,
  };
}
